import React, { useState } from 'react';
import { useAuthenticatedImage } from '../../../../hooks/useAuthenticatedImage';
import { UserColor } from '../const/UserColors';
import { adjustFontSizeAvatar } from '../helpers/UserIntialsHelper';
import { buildProfilePhotoThumbnailUrl } from '../../../profile-photo/profilePhotoThumbnailUrl';
import { useNearViewport } from '../hooks/useNearViewport';

type KanbanCardAvatarProps = {
  initials: string;
  relationId: number;
  hasProfilePhoto: boolean;
  profilePhotoUrl?: string | null;
  className?: string;
  colorKey?: string;
};

/**
 * Presentation-only avatar for the Kanban card hot path.
 * Profile photo resolution is performed while building the board response.
 */
const KanbanCardAvatar: React.FC<KanbanCardAvatarProps> = ({
  initials,
  relationId,
  hasProfilePhoto,
  profilePhotoUrl,
  className = 'kanban-card__avatar',
  colorKey = initials,
}) => {
  const [failedPhotoUrl, setFailedPhotoUrl] = useState<string | null>(null);
  const { elementRef, isNearViewport } = useNearViewport<HTMLDivElement>(hasProfilePhoto);
  const photoUrl = isNearViewport
    ? buildProfilePhotoThumbnailUrl(relationId, hasProfilePhoto, profilePhotoUrl)
    : null;
  const authenticatedPhotoUrl = useAuthenticatedImage(photoUrl);
  const shouldShowPhoto = Boolean(authenticatedPhotoUrl) && failedPhotoUrl !== photoUrl;
  const color = UserColor.getColor(colorKey);

  return (
    <div
      ref={elementRef}
      className={className}
      style={{
        backgroundColor: color.backgroundColor,
        border: `2px solid ${color.borderColor}`,
        fontSize: `${adjustFontSizeAvatar(initials.length)}px`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {initials}
      {shouldShowPhoto && (
        <img
          src={authenticatedPhotoUrl ?? undefined}
          alt="Profile photo"
          loading="lazy"
          decoding="async"
          onError={() => setFailedPhotoUrl(photoUrl)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      )}
    </div>
  );
};

export default KanbanCardAvatar;
